import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/contexts/ToastContext";
import { CheckCircle2, XCircle, Loader2, Check, X, Info } from "lucide-react";
import AuthLayout from "@/components/layout/AuthLayout";
import api from "@/lib/api";

function getPasswordStrength(pw: string): number {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  return score;
}

const strengthLabels = ["Weak", "Fair", "Good", "Strong"];
const strengthColors = ["bg-red-500", "bg-amber-500", "bg-blue-500", "bg-green-500"];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const token = searchParams.get("token");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirmPassword?: string }>(
    {}
  );

  useEffect(() => {
    if (!token) {
      setStatus("error");
    }
  }, [token]);

  const headlineMap: Record<string, string> = {
    idle: "Almost there.",
    loading: "Almost there.",
    success: "You're all set.",
    error: "Almost there.",
  };

  const subtextMap: Record<string, string> = {
    idle: "Choose a strong new password to secure your account.",
    loading: "Resetting your password now...",
    success: "Your password has been reset and your account is secured.",
    error: "Something went wrong with your reset link.",
  };

  const validateFields = () => {
    const errors: { password?: string; confirmPassword?: string } = {};
    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateFields()) return;

    if (!token) {
      toast.error("Invalid reset token. Please request a new reset link.");
      setStatus("error");
      return;
    }

    setStatus("loading");

    try {
      await api.post("/auth/reset-password", { token, password });
      setStatus("success");
      toast.success("Password reset successfully!");

      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error: any) {
      setStatus("idle");
      const errorMessage = error.response?.data?.message || "";
      toast.error(
        errorMessage ||
          "Failed to reset password. The link may have expired. Please request a new reset link."
      );
    }
  };

  const strength = password ? getPasswordStrength(password) : 0;

  return (
    <AuthLayout headline={headlineMap[status]} subtext={subtextMap[status]}>
      <div className="space-y-6 animate-fade-in-up">
        {/* --- Success state --- */}
        {status === "success" && (
          <div className="text-center space-y-5">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-50 ring-8 ring-green-50/50">
              <CheckCircle2 className="h-9 w-9 text-green-600" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Password reset successful
              </h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                Your password has been reset. Redirecting you to sign in...
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <p className="text-sm text-green-800 text-left">
                  You can now sign in with your new password.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/login")} className="w-full" size="lg">
              Go to sign in
            </Button>
          </div>
        )}

        {/* --- Error state (invalid/missing token) --- */}
        {status === "error" && (
          <div className="text-center space-y-5">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 ring-8 ring-red-50/50">
              <XCircle className="h-9 w-9 text-red-600" />
            </div>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Invalid reset link</h2>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-red-600 mt-0.5 shrink-0" />
                <p className="text-sm text-red-800 text-left">
                  Password reset links expire after a short time for security. Go to sign in and use
                  "Forgot password?" to get a new link.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate("/login")} className="w-full" size="lg">
              Go to sign in
            </Button>
          </div>
        )}

        {/* --- Form state --- */}
        {(status === "idle" || status === "loading") && (
          <>
            <div>
              <h2 className="font-heading text-2xl font-bold text-slate-900">
                Reset your password
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Enter a new password to secure your account.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="password">New password</Label>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password)
                      setFieldErrors((p) => ({ ...p, password: undefined }));
                  }}
                  placeholder="Enter new password"
                  disabled={status === "loading"}
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.password}
                  className={
                    fieldErrors.password
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                />
                {fieldErrors.password && (
                  <p className="text-sm text-red-600" role="alert">
                    {fieldErrors.password}
                  </p>
                )}

                {password && (
                  <div className="space-y-2 pt-1">
                    <div className="flex gap-1.5">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`h-1.5 flex-1 rounded-full transition-colors ${
                            i < strength ? strengthColors[strength - 1] : "bg-slate-200"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-xs text-slate-500">
                      Strength: {strengthLabels[strength - 1] || "Too short"}
                    </p>

                    <div className="space-y-1">
                      {[
                        { met: password.length >= 8, label: "At least 8 characters" },
                        {
                          met: /[a-z]/.test(password) && /[A-Z]/.test(password),
                          label: "Upper & lowercase letters",
                        },
                        { met: /\d/.test(password), label: "At least one number" },
                        {
                          met: /[^a-zA-Z0-9]/.test(password),
                          label: "At least one special character",
                        },
                      ].map((req) => (
                        <div key={req.label} className="flex items-center gap-2 text-xs">
                          {req.met ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <X className="h-3.5 w-3.5 text-slate-400" />
                          )}
                          <span className={req.met ? "text-green-700" : "text-slate-500"}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confirmPassword">Confirm new password</Label>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (fieldErrors.confirmPassword)
                      setFieldErrors((p) => ({ ...p, confirmPassword: undefined }));
                  }}
                  placeholder="Confirm new password"
                  disabled={status === "loading"}
                  autoComplete="new-password"
                  aria-invalid={!!fieldErrors.confirmPassword}
                  className={
                    fieldErrors.confirmPassword
                      ? "border-red-400 focus-visible:border-red-400 focus-visible:ring-red-100"
                      : ""
                  }
                />
                {confirmPassword && password === confirmPassword && (
                  <p className="flex items-center gap-1.5 text-xs text-green-600">
                    <Check className="h-3.5 w-3.5" /> Passwords match
                  </p>
                )}
                {fieldErrors.confirmPassword && (
                  <p className="text-sm text-red-600" role="alert">
                    {fieldErrors.confirmPassword}
                  </p>
                )}
              </div>

              <Button type="submit" disabled={status === "loading"} className="w-full" size="lg">
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting password...
                  </>
                ) : (
                  "Reset password"
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500">
              Remember your password?{" "}
              <Link
                to="/login"
                className="text-primary font-medium hover:text-primary-600 transition-colors"
              >
                Sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </AuthLayout>
  );
}
